export type AreaId =
  | 'utveckling'
  | 'test'
  | 'krav'
  | 'projektledning'
  | 'data-ai'
  | 'infra-cloud'
  | 'sakerhet'
  | 'ux-design'
  | 'arkitektur'
  | 'ledning'
  | 'ovrigt';

export type SourceId =
  | 'techrelations'
  | 'keyman'
  | 'interagent'
  | 'cinode'
  | 'emagine'
  | 'nikita'
  | 'pro4u'
  | 'regent'
  | 'upgraded'
  | 'wise'
  | 'asociety'
  | 'developersbay'
  | 'konsultfabriken'
  | 'verama'
  | 'deploja'
  | 'rightpeople'
  | 'itcnetwork'
  | 'houseofskills'
  | 'sigma'
  | 'randstad'
  | 'experis'
  | 'chas'
  | 'biolit';

export interface TechStat {
  name: string;
  category: string;
  count: number;
  /** Antal de senaste 30 dagarna, räknat på verkliga publiceringsdatum. */
  currentCount: number;
  /** Antal de 30 dagarna dessförinnan. */
  previousCount: number;
  /** null = underlaget räcker inte för att uttala sig. Inte samma sak som 0. */
  trendPct: number | null;
}

export interface AggregateBundle {
  generatedAt: string;
  totalAssignments: number;
  /** Antal uppdrag där månaden är gissad ur upptäcktsdatum. */
  estimatedDateAssignments: number;
  /** monthKey -> hur många av månadens uppdrag som saknar publiceringsdatum. */
  estimatedByMonth: Record<string, number>;
  months: string[];
  sources: { id: SourceId; name: string; url: string; count: number }[];
  /** count = huvudområde, mentioned = alla uppdrag som nämner området. */
  areas: { id: AreaId; name: string; count: number; mentioned: number }[];
  technologies: TechStat[];
  areaByMonth: Record<string, Partial<Record<AreaId, number>>>;
  techByMonth: Record<string, Record<string, number>>;
  techByArea: Record<string, { name: string; count: number }[]>;
  sourceByMonth: Record<string, Partial<Record<SourceId, number>>>;
  topClients: { name: string; count: number }[];
  topLocations: { name: string; count: number }[];
  /** Tekniker som ofta efterfrågas tillsammans. */
  techPairs: { a: string; b: string; count: number }[];
  /** Samma par, inom varje område. */
  techPairsByArea: Record<string, { a: string; b: string; count: number }[]>;
  /** Tekniker sorterade efter när de först dök upp, nyast först. */
  emergingTechnologies: { name: string; category: string; firstMonth: string; count: number }[];
  /** monthKey -> antal per arbetssätt */
  workModeByMonth: Record<string, Partial<Record<string, number>>>;
  /** monthKey -> antal per senioritetsnivå */
  seniorityByMonth: Record<string, Record<string, number>>;
}

export interface AssignmentListItem {
  id: string;
  source: SourceId;
  externalId: string;
  title: string;
  url: string;
  publishedAt?: string;
  deadlineAt?: string;
  startAt?: string;
  endAt?: string;
  location?: string;
  country?: string;
  client?: string;
  excerpt?: string;
  sourceCategories?: string[];
  sourceSkills?: string[];
  workMode?: 'onsite' | 'hybrid' | 'remote' | 'unknown';
  area: AreaId;
  areas: AreaId[];
  technologies: string[];
  seniority?: 'junior' | 'mid' | 'senior' | 'lead';
  firstSeen: string;
  lastSeen: string;
  monthKey: string;
  year: number;
  month: number;
  dateEstimated: boolean;
  /** Sattes när annonsen slutade svara hos källan. */
  removedAt?: string;
  /** Id på den rad som representerar uppdraget när flera mäklare annonserar det. */
  duplicateGroup?: string;
  /** Samtliga källor som annonserar uppdraget. */
  duplicateSources?: SourceId[];
}

export interface AssignmentPage {
  total: number;
  page: number;
  pageSize: number;
  items: AssignmentListItem[];
}

export interface ScrapeResult {
  source: SourceId;
  ok: boolean;
  fetched: number;
  errorMessage?: string;
  durationMs: number;
}

export interface Meta {
  areas: { id: AreaId; name: string }[];
  sources: { id: SourceId; name: string; url: string; supportsHistory: boolean }[];
  techCategories: string[];
  lastRun?: {
    runId: string;
    startedAt: string;
    finishedAt: string;
    mode: string;
    results: ScrapeResult[];
    newAssignments: number;
    updatedAssignments: number;
  };
}

export interface Filters {
  year?: number;
  month?: number;
  area?: AreaId;
  tech: string[];
  source?: SourceId;
  location?: string;
  seniority?: string;
  workMode?: string;
  search?: string;
  datedOnly?: boolean;
  hideRemoved?: boolean;
  openOnly?: boolean;
  /** Visa varje mäklares annons för sig i stället för en rad per uppdrag. */
  showDuplicates?: boolean;
}

export const EMPTY_FILTERS: Filters = { tech: [] };
